/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * 패널 클래스를 정의한다.
 *
 * @file /src/components/viewer/Viewer.ts
 * @author sungjin <esung246@naddle.net>
 * @license MIT License
 * @modified 2026. 10. 12.
 */
import * as parent from '@components/tab/Tab';
import { Menu } from '@components/menu/Menu';
import { Form } from '@components/form/Form';
import { Hidden } from '@components/form/field/hidden/Hidden';
import { Container } from '@components/form/field/container/Container';
import { Select } from '@components/form/field/select/Select';
import { Text } from '@components/form/field/text/Text';
import { Window } from '@components/window/Window';
import { Message } from '@components/message/Message';
import { View } from '@components/viewer/View';
import { Component } from '@base/Component';
import { Local } from '@models/store/list/Local';
import { Button } from '@namespaces/components/Button';
import { Format } from '@utils/Format';
import { Ajax } from '@utils/Ajax';
import { Language } from '@utils/Language';

export interface Listeners<T> extends parent.Listeners<T> {}

export interface Properties<T> extends parent.Properties<T> {
    /**
     * 뷰어를 처리할 URL
     */
    url: string;

    /**
     * 기본 뷰
     */
    view: View;

    /**
     * 상단바
     */
    topbar?: (string | Component)[];
}

export class Viewer extends parent.Tab {
    /**
     * 컴포넌트 확장타입
     */
    variant = 'viewer';

    /**
     * Task 의 기본주소
     */
    baseUrl: string | null;

    /**
     * 뷰어를 처리할 URL
     */
    url: string;

    /**
     * 탭 패널 클래스를 정의한다.
     *
     * @param properties - 초기설정
     */
    constructor(
        public properties: Pick<Properties<Viewer>, 'name' | 'layout' | 'url' | 'view' | 'topbar' | 'listeners'> = null
    ) {
        properties.view.setName('all');

        if (properties.name) {
            properties.view.setResizeNm('viewers.' + properties.name);
        }

        super({ ...properties, topbar: null, items: [properties.view ?? null, null] });

        properties.topbar ??= [];
        if (properties.topbar.indexOf('->') == -1) {
            properties.topbar.push('->');
        }

        properties.topbar.push(this.getButton());

        this.topbars = this.createToolbar(properties.topbar, 'top');

        this.addEvent('sorter', async (list) => {
            await this.sorter(list);
        });

        this.addEvent('close', async (view: View) => {
            await this.delete(view);
        });

        this.url = properties.url;
    }

    /**
     * 보기 저장 버튼을 가져온다.
     *
     * @returns 보기 저장 버튼
     */
    getButton(): Button {
        return new Button({
            text: Language.get('components.viewer.save_view'),
            icon: 'xi xi-tagged-book',
            buttonClass: 'confirm',
            menu: new Menu({
                menus: [
                    {
                        type: 'button',
                        icon: 'mi mi-save',
                        text: Language.get('buttons.save'),
                        handler: async (me) => {
                            const viewer = me.getComponent().getParents(Viewer);
                            if (viewer !== null) {
                                const view = viewer.getActiveTab();
                                viewer.save(view);
                            }

                            return true;
                        },
                    },
                    {
                        type: 'button',
                        icon: 'mi mi-edit',
                        text: Language.get('components.viewer.edit'),
                        handler: async (me) => {
                            const viewer = me.getComponent().getParents(Viewer);
                            if (viewer !== null) {
                                const view = viewer.getActiveTab();
                                viewer.create(view, view.getName());
                            }

                            return true;
                        },
                    },
                    {
                        type: 'button',
                        icon: 'mi mi-placeholder-plus',
                        text: Language.get('components.viewer.save_as'),
                        handler: async (me) => {
                            const viewer = me.getComponent().getParents(Viewer);
                            if (viewer !== null) {
                                const view = viewer.getActiveTab();
                                viewer.create(view);
                            }

                            return true;
                        },
                    },
                    '-',
                    {
                        type: 'button',
                        icon: 'mi mi-eraser',
                        text: Language.get('components.viewer.reset_filters'),
                        handler: async (me) => {
                            const viewer = me.getComponent().getParents(Viewer);
                            if (viewer !== null) {
                                const view = viewer.getActiveTab();
                                view.getList().getStore().setFilters(null);
                            }

                            return true;
                        },
                    },
                ],
                listeners: {
                    show: (menu) => {
                        const viewer = menu.getParents(Viewer);
                        if (viewer !== null) {
                            const view = viewer.getActiveTab();
                            if (view?.getName() === 'all') {
                                menu.menus[0].hide();
                                menu.menus[1].hide();
                            } else {
                                menu.menus[0].show();
                                menu.menus[1].show();
                            }
                        }
                    },
                },
            }),
            handler: async (button) => {
                button.setLoading(true);
                const viewer = button.getParents(Viewer);
                if (viewer !== null) {
                    const view = viewer.getActiveTab();
                    await viewer.save(view);
                }
                button.setLoading(false);
            },
        });
    }

    /**
     * 신규 보기를 생성한다.
     *
     * @param view - 보기
     * @param viewer_id - 보기 수정일 경우 보기 고유값
     */
    create(view: View, viewer_id: string = null): void {
        new Window({
            title: Language.get(viewer_id !== null ? 'components.viewer.edit' : 'components.viewer.add'),
            width: 400,
            modal: true,
            resizable: false,
            items: [
                new Form({
                    border: false,
                    layout: 'fit',
                    items: [
                        new Hidden({
                            name: 'filters',
                        }),
                        new Hidden({
                            name: 'sorters',
                        }),
                        new Hidden({
                            name: 'grouper',
                        }),
                        new Container({
                            direction: 'row',
                            items: [
                                new Select({
                                    name: 'icon',
                                    store: new Local({
                                        provider: {
                                            dataFields: ['value'],
                                            datas: [
                                                ['xi xi-bars'],
                                                ['xi xi-new'],
                                                ['xi xi-calendar'],
                                                ['xi xi-calendar-week'],
                                                ['xi xi-calendar-month'],
                                                ['xi xi-home'],
                                                ['xi xi-plus'],
                                                ['xi xi-close'],
                                                ['xi xi-archive'],
                                                ['xi xi-exchange'],
                                                ['xi xi-cart'],
                                                ['xi xi-box'],
                                                ['xi xi-box'],
                                                ['xi xi-education'],
                                                ['xi xi-calculator'],
                                                ['xi xi-pen'],
                                                ['xi xi-magnifier'],
                                                ['xi xi-trash'],
                                                ['xi xi-pin'],
                                                ['xi xi-clip'],
                                                ['xi xi-slip-tongs'],
                                                ['xi xi-canvas'],
                                                ['xi xi-user-check'],
                                                ['xi xi-lecture'],
                                                ['xi xi-at'],
                                                ['xi xi-chat'],
                                                ['xi xi-star'],
                                                ['xi xi-heart'],
                                                ['xi xi-flag-triangle'],
                                                ['xi xi-trophy'],
                                                ['xi xi-form-checkout'],
                                            ],
                                        },
                                    }),
                                    search: false,
                                    initOnChange: false,
                                    width: 60,
                                    displayField: 'value',
                                    valueField: 'value',
                                    value: 'xi xi-bars',
                                    listRenderer: (value) => {
                                        return '<i class="' + value + '"></i>';
                                    },
                                    renderer: (value) => {
                                        return '<i class="' + value + '"></i>';
                                    },
                                }),
                                new Text({
                                    name: 'title',
                                    placeholder: Language.get('components.viewer.name'),
                                    allowBlank: false,
                                    flex: 1,
                                    value: null,
                                }),
                            ],
                            helpText: Language.get('components.viewer.help'),
                        }),
                    ],
                }),
            ],
            buttons: [
                new Button({
                    text: Language.get('buttons.cancel'),
                    handler: (button) => {
                        const window = button.getParents(Window);
                        window.close();
                    },
                }),
                new Button({
                    text: Language.get('buttons.ok'),
                    buttonClass: 'confirm',
                    handler: async (button) => {
                        const window = button.getParents(Window);
                        const form = window.getItemAt<Form>(0);
                        const grid = view.getList();
                        form.getField('filters').setValue(
                            Object.keys(grid.getStore().getFilters()).length === 0 ? null : grid.getStore().getFilters()
                        );
                        form.getField('sorters').setValue(
                            Object.keys(grid.getStore().getSorters()).length === 0 ? null : grid.getStore().getSorters()
                        );
                        if (grid.grouper != null) {
                            form.getField('grouper').setValue({
                                dataIndex: grid.grouper.dataIndex,
                                sorters: grid.grouper.sorters,
                                summary: grid.grouper.summary === true ? 'TRUE' : 'FALSE',
                            });
                        } else {
                            form.getField('grouper').setValue(null);
                        }
                        const results = await form.submit({
                            url: `${this.url}/${viewer_id !== null ? viewer_id : ''}`,
                        });
                        if (results.success == true) {
                            const viewer = view.getViewer();
                            const icon = await form.getField('icon').getValue();
                            const title = await form.getField('title').getValue();
                            const filters = await form.getField('filters').getValue();
                            const sorters = await form.getField('sorters').getValue();
                            const grouper = await form.getField('grouper').getValue();

                            if (viewer_id !== null) {
                                const panel = viewer.getItemByName(viewer_id);
                                const tab = viewer.getTabbar().getTab(panel.getId());
                                tab.setIcon(icon);
                                tab.setText(title);
                            } else {
                                const clone = this.properties.view.clone({
                                    name: results.viewer_id,
                                    icon: icon,
                                    title: title,
                                    closable: true,
                                    movable: true,
                                });
                                if (this.name !== null) {
                                    clone.setResizeNm('viewers.' + this.name);
                                }
                                clone.getList().getStore().setFilters(filters);
                                clone.getList().getStore().setSorters(sorters);
                                clone.getList().getStore().setGroup(grouper);

                                await viewer.appendItem(clone);
                                await viewer.active(clone.getId());
                            }
                            window.close();
                        }
                    },
                }),
            ],
            listeners: {
                show: async (window) => {
                    if (viewer_id !== null) {
                        const form = window.getItemAt<Form>(0);
                        const results = await form.load({ url: `${this.url}/${viewer_id}` });
                        if (results.success == false) {
                            window.close();
                        }
                    }
                },
            },
        }).show();
    }

    /**
     * 현재 활성화된 보기를 가져온다.
     *
     * @returns 현재 활성화된 보기
     */
    getActiveTab<T = View>(): T {
        return super.getActiveTab() as T;
    }

    /**
     * 현재 보기를 저장한다.
     *
     * @param view - 현재 보기
     */
    async save(view: View): Promise<void> {
        const viewer_id = view.getName();
        if (viewer_id == 'all') {
            this.create(view);
            return;
        }

        const grid = view.getList();
        const data = {
            'filters': Object.keys(grid.getStore().getFilters()).length === 0 ? null : grid.getStore().getFilters(),
            'sorters': Object.keys(grid.getStore().getSorters()).length === 0 ? null : grid.getStore().getSorters(),
            'grouper':
                grid.grouper != null
                    ? {
                          dataIndex: grid.grouper.dataIndex,
                          sorters: grid.grouper.sorters,
                          summary: grid.grouper.summary === true ? 'TRUE' : 'FALSE',
                      }
                    : null,
        };

        const results = await Ajax.post(`${this.url}/${viewer_id}`, data);

        if (results.success == true) {
            Message.show({
                title: Language.get('texts.info'),
                message: Language.getAction('SAVED'),
                icon: Message.INFO,
                buttons: Message.OK,
            });
        }
    }

    /**
     * 보기 목록 순서를 변경한다.
     *
     * @param records - 보기 목록
     */
    async sorter(records: { name?: string; title: string; icon: string }[]): Promise<void> {
        await Ajax.patch(this.url, { records });
    }

    /**
     * 보기를 삭제한다.
     *
     * @param view - 보기 패널
     */
    async delete(view: View): Promise<void> {
        if (view === null || view.getName() === null) return;

        await Ajax.delete(`${this.url}/${view.getName()}`);
    }

    /**
     * 컴포넌트의 이름을 설정한다.
     *
     * @param name - 이름
     */
    setName(name: string): void {
        this.name = name;
        if (this.$component !== null) {
            this.$component.setAttr('data-name', this.name);
        }

        for (const item of this.items as View[]) {
            item.setResizeNm('viewers.' + this.name);
        }
    }

    /**
     * Task 의 기본 URL 을 가져온다.
     *
     * @returns 기본 URL
     */
    async getUrl(): Promise<string | null> {
        if (this.baseUrl === undefined) {
            this.baseUrl = Format.rtrim((await this.getTask()?.getUrl(true)) ?? null, '/');
        }

        return this.baseUrl;
    }

    /**
     * 기본 URL 을 제외한 경로의 특정 인덱스의 값을 가져온다.
     *
     * @returns path
     */
    async getPathAt(index: number): Promise<string | null> {
        const baseUrl = await this.getUrl();
        if (baseUrl === null) {
            return null;
        }

        const currentUrl = (await this.getTask().getUrl()) ?? null;
        const paths = Format.trim(currentUrl.replace(new RegExp(`^${baseUrl}/?`), ''), '/')
            .split('/')
            .filter((value) => (value?.length > 0 ? value : null));

        return paths[index] ?? null;
    }

    /**
     * 기본 URL 에 추가경로를 설정한다.
     *
     * @params path
     */
    async setPath(path: string): Promise<void> {
        const baseUrl = await this.getUrl();
        if (baseUrl === null) {
            return;
        }

        path = Format.trim(path, '/');
        await this.getTask()?.setUrl(baseUrl + '/' + path);
    }

    /**
     * 컴포넌트 렌더링이 완료된 이후 초기상태정의가 필요할 경우, 상태를 렌더링한다.
     */
    async renderState(): Promise<void> {
        const results = await Ajax.get(this.url);
        if (results.success == true) {
            for (const config of results.records) {
                const view = this.properties.view.clone({
                    name: config.viewer_id,
                    icon: config.icon,
                    title: config.title,
                    closable: true,
                    movable: true,
                });
                if (this.name !== null) {
                    view.setResizeNm('viewers.' + this.name);
                }
                view.getList().getStore().setFilters(config.filters);
                view.getList().getStore().setSorters(config.sorters);
                view.getList().getStore().setGroup(config.grouper);

                await this.appendItem(view);
            }
        }

        const view_id = (await this.getPathAt(0)) ?? 'all';
        const view = this.getItemByName(view_id) ?? this.getItemAt(0);
        await this.active(view.getId());

        await super.renderState();
    }

    /**
     * 활성화된 탭이 변경되었을 때
     *
     * @param panel - 활성화된 탭패널
     */
    async onActive(panel: View): Promise<void> {
        const view_id = await this.getPathAt(0);
        if (view_id != panel.getName()) {
            this.setPath(panel.getName());
        }

        const list = panel.getList();
        const select_id = await this.getPathAt(1);

        if (select_id === null) {
            if (list.getStore().isLoaded() == false) {
                await list.getStore().load();
            }
        } else {
            const pk = list.getStore().primaryKeys[0];
            if (list.getStore().isLoaded() == false) {
                await list.getStore().loadPage({ [pk]: select_id });
            }

            const record = await list.getStore().find({ [pk]: select_id });
            if (record !== null) {
                list.getSelection().select(record);
            }
        }

        await this.fireEvent('active', [panel, this]);
    }
}
